import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PairingRecipes } from "@/components/RecipeBlock";
import { MoviePoster } from "@/components/MoviePoster";
import { MovieNightMode } from "@/components/MovieNightMode";
import { ShoppingList } from "@/components/ShoppingList";
import { StarRating } from "@/components/StarRating";
import { ReelLoader } from "@/components/ReelLoader";
import { Clapperboard, ShoppingCart } from "lucide-react";

export default function PublicPairing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = user && user !== null;
  const [pairing, setPairing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [nightMode, setNightMode] = useState(false);
  const [shopping, setShopping] = useState(false);

  useEffect(() => {
    api
      .get(`/pairings/public/${id}`)
      .then((res) => setPairing(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen pt-32 text-center px-5" data-testid="public-not-found">
        <Clapperboard className="w-14 h-14 text-[#E50914] mx-auto mb-5" />
        <h1 className="font-display text-3xl font-bold mb-3">This pairing isn't available</h1>
        <p className="text-white/50 mb-6">It may be private or no longer exists.</p>
        <Button onClick={() => navigate("/community")} className="rounded-full bg-[#E50914] hover:bg-[#c00811]">
          Browse the Community
        </Button>
      </div>
    );
  }

  if (!pairing) {
    return (
      <div className="min-h-screen pt-24">
        <ReelLoader label="Loading pairing..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-5 sm:px-8" data-testid="public-pairing-page">
      <div className="max-w-4xl mx-auto animate-fade-up">
        <div className="flex flex-col sm:flex-row gap-6 items-start mb-8">
          <MoviePoster
            title={pairing.movie_title}
            className="w-40 h-56 rounded-2xl shrink-0 shadow-2xl"
          />
          <div className="pt-2">
            <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">
              Shared Pairing · by {pairing.username}
            </span>
            <h1 className="font-display text-4xl sm:text-6xl font-black mt-2 mb-4">
              {pairing.movie_title}
            </h1>
            {pairing.rating > 0 && <StarRating value={pairing.rating} readOnly size={22} />}
            {pairing.theme_description && (
              <p className="text-lg font-light leading-relaxed text-white/85 mt-4 max-w-2xl">
                {pairing.theme_description}
              </p>
            )}
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => setNightMode(true)}
            data-testid="movie-night-btn"
            className="w-full sm:w-auto rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white h-12 px-8"
          >
            <Clapperboard className="w-5 h-5 mr-2 text-[#E50914]" />
            Start Movie Night Mode
          </Button>
          <Button
            onClick={() => setShopping(true)}
            data-testid="shopping-list-btn"
            className="w-full sm:w-auto rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white h-12 px-8"
          >
            <ShoppingCart className="w-5 h-5 mr-2 text-[#5EC5A0]" />
            Shopping List
          </Button>
        </div>

        <PairingRecipes pairing={pairing} />

        <div className="mt-10 rounded-2xl border border-[#E50914]/30 bg-[#E50914]/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/80">
            Want to create your own movie dinner pairings?
          </p>
          <Button
            onClick={() => navigate(isAuthed ? "/generate" : "/register")}
            data-testid="public-cta-btn"
            className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-11 px-8 shrink-0"
          >
            {isAuthed ? "Generate a Pairing" : "Sign Up Free"}
          </Button>
        </div>
      </div>

      <MovieNightMode
        pairing={pairing}
        open={nightMode}
        onClose={() => setNightMode(false)}
      />
      <ShoppingList
        pairing={pairing}
        open={shopping}
        onClose={() => setShopping(false)}
      />
    </div>
  );
}
